import { apiSlice } from '../../../api/common/apiSlice';
import { ApiTagType } from '../../../constants/apiTagType';
import {
  AccountDeletionEligibility,
  AccountDeletionRequest,
  FetchAccountDeletionRequestsArgs,
  FetchAccountDeletionRequestsResponse,
} from '../../../types/accountDeletion';

/** Any action changes the request and moves it between the queue's tabs, so the list and its live checks refetch. */
const invalidateRequest = (id: number) => [
  { type: ApiTagType.AccountDeletionRequest as const, id },
  { type: ApiTagType.AccountDeletionRequest as const, id: 'LIST' },
  { type: ApiTagType.AccountDeletionBlockers as const, id },
];

/** Account deletion requests (MAD-5179), through madkhol-admin's `/admin/account-deletion` routes. */
export const accountDeletionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchAccountDeletionRequests: builder.query<FetchAccountDeletionRequestsResponse, FetchAccountDeletionRequestsArgs>(
      {
        // Only the filters madkhol-user accepts: it refuses any other query parameter.
        query: ({ status, readyOnly, offset, limit }) => ({
          url: '/admin/account-deletion',
          method: 'GET',
          params: { ...(status ? { status } : {}), ...(readyOnly ? { readyOnly: 'true' } : {}), offset, limit },
        }),
        providesTags: (res) => [
          ...(res?.items || []).map(({ id }) => ({ type: ApiTagType.AccountDeletionRequest as const, id })),
          { type: ApiTagType.AccountDeletionRequest, id: 'LIST' },
        ],
      },
    ),
    fetchAccountDeletionBlockers: builder.query<AccountDeletionEligibility, number>({
      query: (id) => ({
        url: `/admin/account-deletion/${id}/blockers`,
        method: 'GET',
      }),
      providesTags: (res, error, id) => [{ type: ApiTagType.AccountDeletionBlockers, id }],
    }),
    cancelAccountDeletion: builder.mutation<AccountDeletionRequest, number>({
      query: (id) => ({
        url: `/admin/account-deletion/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (res, error, id) => invalidateRequest(id),
    }),
    startAccountDeletion: builder.mutation<AccountDeletionRequest, number>({
      query: (id) => ({
        url: `/admin/account-deletion/${id}/start`,
        method: 'POST',
      }),
      invalidatesTags: (res, error, id) => invalidateRequest(id),
    }),
    completeAccountDeletion: builder.mutation<AccountDeletionRequest, number>({
      query: (id) => ({
        url: `/admin/account-deletion/${id}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (res, error, id) => invalidateRequest(id),
    }),
  }),
});

export const {
  useFetchAccountDeletionRequestsQuery,
  useFetchAccountDeletionBlockersQuery,
  useCancelAccountDeletionMutation,
  useStartAccountDeletionMutation,
  useCompleteAccountDeletionMutation,
} = accountDeletionApiSlice;
